import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors } from '@angular/forms';
import { resolveShopPluginCdn } from '@app/pages/checkout-app/utils';
import {
  CustomCodeFormShape,
  CustomCodeVariableDoc,
  createCustomCodeFormGroup,
} from '../custom-code/custom-code.model';
import { createTimerFormGroup, TimerFormShape } from '../../components/timer-config/timer.model';
import { DEFAULT_DISCOUNT_LABEL_COLOR, DiscountType } from '../../discount.model';

export type MinimumThresholdType = 'cart_value' | 'item_count';
export type GoalsBarType = 'sequential' | 'inline' | 'single_bar';
export type GoalsPadding = 'none' | 'small' | 'medium' | 'large';
export type GoalOfferType = 'freeShipping' | 'orderDiscount' | 'product';
// Values mirror GiftUnlockExtension.GiftMode / ProductDisplayType.
export type GiftMode = 'single' | 'multiple';
export type ProductDisplayType = 'list' | 'compact';
export type LabelPosition = 'above_bar' | 'below_bar';
// Inline / single-bar: which label (1st or 2nd) is shown on the completed pill.
export type PillSource = 'first' | 'second';
export type ImageFit = 'square' | 'crop' | 'fill';

// Preset icons from the shop-plugin CDN. Uses the colored ("-color") variant, mirroring the product-page promise.
const goalIconCdn = resolveShopPluginCdn();
export const GOAL_PRESET_ICON_NAMES = ['coupon', 'diamond', 'shield', 'truck', 'gift'] as const;
export const GOAL_PRESET_ICON_URLS: string[] = GOAL_PRESET_ICON_NAMES.map(
  (name) => `${goalIconCdn}/icons/${name}-color.svg`,
);

export interface GoalOfferProduct {
  id: string | number;
  handle?: string;
  name: string;
  description?: string;
  price?: string;
  imageUrl?: string;
  sku?: string;
  priority?: number;
  selectedOptions?: { name: string; values: string[] }[];
}

export interface GoalThresholdFormShape {
  countriesAndRegions: FormControl<Record<string, string[]>>;
  threshold: FormControl<number>;
  currency: FormControl<string>;
}

export interface GoalOrderDiscountFormShape {
  discountType: FormControl<DiscountType>;
  value: FormControl<number>;
  labelText: FormControl<string>;
  labelColor: FormControl<string>;
  autoApply: FormControl<boolean>;
}

export interface GoalProductOfferFormShape {
  giftMode: FormControl<GiftMode>;
  productDisplayType: FormControl<ProductDisplayType>;
  products: FormControl<GoalOfferProduct[]>;
  hideIfProductIsInCart: FormControl<boolean>;
  discountConfiguration: FormGroup<{
    enabled: FormControl<boolean>;
    discountType: FormControl<DiscountType>;
    value: FormControl<number>;
    labelText: FormControl<string>;
    labelColor: FormControl<string>;
  }>;
}

export interface GoalOfferFormShape {
  type: FormControl<GoalOfferType>;
  orderDiscount: FormGroup<GoalOrderDiscountFormShape>;
  product: FormGroup<GoalProductOfferFormShape>;
}

export interface GoalApplySectionFormShape {
  background: FormGroup<{
    color: FormControl<string>;
    padding: FormControl<GoalsPadding>;
    cornerRadius: FormControl<number>;
    border: FormGroup<{
      enabled: FormControl<boolean>;
      color: FormControl<string>;
      width: FormControl<number>;
    }>;
  }>;
  texts: FormGroup<{
    title: FormControl<string>;
    subtitle: FormControl<string>;
    orderDiscountMessage: FormControl<string>;
    textColor: FormControl<string>;
    fontSize: FormControl<number>;
  }>;
  // Design fields are nullable: null means "inherit from global button design".
  button: FormGroup<{
    label: FormControl<string>;
    backgroundColor: FormControl<string | null>;
    textColor: FormControl<string | null>;
    hoverColor: FormControl<string | null>;
    cornerRadius: FormControl<number | null>;
  }>;
  // Product-offer only.
  image: FormGroup<{
    fit: FormControl<ImageFit>;
    borderWidth: FormControl<number>;
    cornerRadius: FormControl<number>;
    innerPadding: FormControl<number>;
  }>;
}

export interface GoalAppearanceFormShape {
  iconUrl: FormControl<string | File>;
  customIconUrl: FormControl<string | File>;
  /** Padding around whichever of this goal's two messages is currently showing. */
  messagePadding: FormControl<GoalsPadding>;
  // Inline / single-bar only: font size (px) for the labels and icon.
  labelSize: FormControl<number>;
  showLabelsInProgressBar: FormControl<boolean>;
  firstLabel: FormControl<string>;
  firstLabelPosition: FormControl<LabelPosition>;
  secondLabel: FormControl<string>;
  secondLabelPosition: FormControl<LabelPosition>;
  // Inline / single-bar: which of the two labels is used as the completed pill.
  pillSource: FormControl<PillSource>;
  // Sequential-bar only: the label shown on this goal's completed pill.
  pillText: FormControl<string>;
  // Order-discount-offer only.
  applySection: FormGroup<GoalApplySectionFormShape>;
}

export interface GoalFormShape {
  /** Frontend-only stable id, used for @for tracking. */
  id: FormControl<string>;
  enabled: FormControl<boolean>;
  thresholds: FormArray<FormGroup<GoalThresholdFormShape>>;
  messageWhenNotMet: FormControl<string>;
  messageWhenMet: FormControl<string>;
  offer: FormGroup<GoalOfferFormShape>;
  appearance: FormGroup<GoalAppearanceFormShape>;
}

export interface GoalsAppearanceFormShape {
  barType: FormControl<GoalsBarType>;
  /** Rule under the whole widget, mirroring the Top Bar's option. */
  bottomBorder: FormGroup<{
    enabled: FormControl<boolean>;
    color: FormControl<string>;
  }>;
  pills: FormGroup<{
    enabled: FormControl<boolean>;
    backgroundColor: FormControl<string>;
    textColor: FormControl<string>;
    cornerRadius: FormControl<number>;
  }>;
  background: FormGroup<{
    color: FormControl<string>;
    padding: FormControl<GoalsPadding>;
    cornerRadius: FormControl<number>;
    border: FormGroup<{
      enabled: FormControl<boolean>;
      color: FormControl<string>;
      width: FormControl<number>;
    }>;
  }>;
  progressBar: FormGroup<{
    color: FormControl<string>;
    barWidth: FormControl<number>;
    cornerRadius: FormControl<number>;
  }>;
}

export interface GoalsProgressBarFormShape {
  minimumThresholdType: FormControl<MinimumThresholdType>;
  appearance: FormGroup<GoalsAppearanceFormShape>;
  goals: FormArray<FormGroup<GoalFormShape>>;
  timer: FormGroup<TimerFormShape>;
  customCode: FormGroup<CustomCodeFormShape>;
}

interface GoalThresholdDefault {
  countriesAndRegions?: Record<string, string[]>;
  threshold?: number;
  currency?: string;
}

interface GoalOfferDefault {
  type?: GoalOfferType;
  orderDiscount?: {
    discountType?: DiscountType;
    value?: number;
    labelText?: string;
    labelColor?: string;
    autoApply?: boolean;
  };
  product?: {
    giftMode?: GiftMode;
    productDisplayType?: ProductDisplayType;
    products?: GoalOfferProduct[];
    hideIfProductIsInCart?: boolean;
    discountConfiguration?: {
      enabled?: boolean;
      discountType?: DiscountType;
      value?: number;
      labelText?: string;
      labelColor?: string;
    };
  };
}

interface GoalApplySectionDefault {
  background?: {
    color?: string;
    padding?: GoalsPadding;
    cornerRadius?: number;
    border?: {
      enabled?: boolean;
      color?: string;
      width?: number;
    };
  };
  texts?: {
    title?: string;
    subtitle?: string;
    orderDiscountMessage?: string;
    textColor?: string;
    fontSize?: number;
  };
  button?: {
    label?: string;
    backgroundColor?: string | null;
    textColor?: string | null;
    hoverColor?: string | null;
    cornerRadius?: number | null;
  };
  image?: {
    fit?: ImageFit;
    borderWidth?: number;
    cornerRadius?: number;
    innerPadding?: number;
  };
}

interface GoalAppearanceDefault {
  iconUrl?: string | File;
  customIconUrl?: string | File;
  messagePadding?: GoalsPadding;
  labelSize?: number;
  showLabelsInProgressBar?: boolean;
  firstLabel?: string;
  firstLabelPosition?: LabelPosition;
  secondLabel?: string;
  secondLabelPosition?: LabelPosition;
  pillSource?: PillSource;
  pillText?: string;
  applySection?: GoalApplySectionDefault;
}

export interface GoalDefault {
  id?: string;
  enabled?: boolean;
  thresholds?: GoalThresholdDefault[];
  messageWhenNotMet?: string;
  messageWhenMet?: string;
  offer?: GoalOfferDefault;
  appearance?: GoalAppearanceDefault;
}

const DEFAULTS = {
  minimumThresholdType: 'cart_value' as MinimumThresholdType,
  appearance: {
    barType: 'sequential' as GoalsBarType,
    // Off by default: the widget has never drawn a rule, so enabling it would restyle every saved cart.
    bottomBorder: { enabled: false, color: '#EEEEEE' },
    pills: {
      enabled: false,
      backgroundColor: '#DDF6DE',
      textColor: '#3E8D36',
      cornerRadius: 0,
    },
    background: {
      color: '#FFFFFF',
      padding: 'small' as GoalsPadding,
      cornerRadius: 0,
      border: {
        enabled: false,
        color: '#FFFFFF',
        width: 0,
      },
    },
    progressBar: {
      color: '#4A7D4A',
      barWidth: 7,
      cornerRadius: 0,
    },
  },
};

// The interactive pieces (bar, message, offer, timer, pills) are rendered by us into the
// `data-pdq-slot` placeholders — merchants position them, we control what's inside. Missing a
// slot just hides that piece. `{{vars}}` are static config; `data-pdq-show-if` toggles per state.
export const GOALS_PROGRESS_BAR_VARIABLES: CustomCodeVariableDoc[] = [
  {
    reference: '{{componentId}}',
    type: 'String',
    description: 'Unique widget ID, useful as a DOM hook for your own CSS or JS',
  },
  {
    reference: '{{minimumThresholdType}}',
    type: 'String',
    description: "How goals are measured: 'cart_value' or 'item_count'",
  },
  { reference: '{{goalCount}}', type: 'Number', description: 'Number of enabled goals' },
  {
    reference: '{{barType}}',
    type: 'String',
    description: "Progress bar style: 'sequential', 'inline' or 'single_bar'",
  },
  { reference: '{{pillsEnabled}}', type: 'Boolean', description: 'True when completed-goal pills are enabled' },
  { reference: '{{timerEnabled}}', type: 'Boolean', description: 'True when the countdown timer is enabled' },
  {
    reference: '{{timerPlacement}}',
    type: 'String',
    description: "Where the timer shows: 'inline', 'badge' or 'in_goal_message'",
  },
  {
    reference: 'data-pdq-slot="progress-bar"',
    type: 'Slot',
    description: 'Placeholder <div> where the progress bar renders',
  },
  {
    reference: 'data-pdq-slot="goal-message"',
    type: 'Slot',
    description: 'The active goal message (tokens like {amount-left} are filled automatically)',
  },
  {
    reference: 'data-pdq-slot="offer"',
    type: 'Slot',
    description: 'The claim-gift / apply-discount area (click handling + Shopify are ours)',
  },
  { reference: 'data-pdq-slot="timer"', type: 'Slot', description: 'The countdown timer' },
  { reference: 'data-pdq-slot="completed-pills"', type: 'Slot', description: 'The completed-goal pills' },
  {
    reference: 'data-pdq-show-if="in-progress"',
    type: 'Directive',
    description: 'Element shows only while at least one goal is not yet reached',
  },
  {
    reference: 'data-pdq-show-if="all-goals-reached"',
    type: 'Directive',
    description: 'Element shows only when every goal has been reached',
  },
];

export const GOALS_PROGRESS_BAR_STARTER_TEMPLATE = `<div
  style="display: flex; flex-direction: column; gap: 12px; padding: 16px 20px;"
>
  <!-- Completed goals. We render the pills; you position the box. -->
  {{#if pillsEnabled}}
    <div data-pdq-slot="completed-pills"></div>
  {{/if}}

  <!-- The active goal message. Tokens like {amount-left} are filled in automatically. -->
  <div data-pdq-slot="goal-message"></div>

  <!-- The progress bar. We draw it; you place it. -->
  <div data-pdq-slot="progress-bar"></div>

  <!-- Countdown timer (only if enabled in the Timer tab). -->
  {{#if timerEnabled}}
    <div data-pdq-slot="timer"></div>
  {{/if}}

  <!-- The offer: claim gift / apply discount. We handle the click and Shopify. -->
  <div data-pdq-slot="offer"></div>

  <!-- Extra text shown only once every goal is reached. -->
  <div data-pdq-show-if="all-goals-reached" style="text-align: center; font-weight: 600;">
    You've unlocked everything 🎉
  </div>
</div>`;

let goalIdCounter = 0;

/** Default "goal not met" message per threshold type. Uses the tag matching the unit ({amount-left} / {items-left}). */
export const GOAL_NOT_MET_DEFAULTS: Record<MinimumThresholdType, string> = {
  cart_value: 'Spend {amount-left} more, get Free Shipping!',
  item_count: 'Add {items-left} more items, get Free Shipping!',
};

/**
 * Validates that, within the same currency and overlapping countries, each goal's threshold is strictly
 * higher than every earlier goal's. Returns a map of `${goalIndex}-${thresholdIndex}` → message so the
 * offending row can render the error inline. Runs on every value change, so errors appear immediately.
 */
export function thresholdOrderValidator(control: AbstractControl): ValidationErrors | null {
  const goals = control as FormArray<FormGroup<GoalFormShape>>;

  interface ThresholdEntry {
    goalIndex: number;
    thresholdIndex: number;
    threshold: number;
    currency: string;
    countries: string[];
  }

  const entries: ThresholdEntry[] = [];
  goals.controls.forEach((goal, goalIndex) => {
    if (goal.controls.enabled.value === false) return;
    goal.controls.thresholds.controls.forEach((threshold, thresholdIndex) => {
      const countriesAndRegions = threshold.controls.countriesAndRegions.value ?? {};
      entries.push({
        goalIndex,
        thresholdIndex,
        threshold: threshold.controls.threshold.value ?? 0,
        currency: threshold.controls.currency.value ?? '',
        countries: Object.keys(countriesAndRegions),
      });
    });
  });

  const errors: Record<string, string> = {};
  for (const entry of entries) {
    if (entry.goalIndex === 0) continue;
    for (const previous of entries) {
      if (previous.goalIndex >= entry.goalIndex) continue;
      if (previous.currency !== entry.currency) continue;
      if (!entry.countries.some((country) => previous.countries.includes(country))) continue;
      if (entry.threshold <= previous.threshold) {
        errors[`${entry.goalIndex}-${entry.thresholdIndex}`] =
          `Goal ${entry.goalIndex + 1} threshold (${entry.threshold}) must be higher than Goal ${previous.goalIndex + 1} threshold (${previous.threshold})`;
        break;
      }
    }
  }

  return Object.keys(errors).length ? { thresholdOrder: errors } : null;
}

export function buildGoalThresholdGroup(
  defaultCountry: string,
  defaultCurrency: string,
  initial?: GoalThresholdDefault,
): FormGroup<GoalThresholdFormShape> {
  return new FormGroup<GoalThresholdFormShape>({
    countriesAndRegions: new FormControl(initial?.countriesAndRegions ?? { [defaultCountry]: [] }, {
      nonNullable: true,
    }),
    threshold: new FormControl(initial?.threshold ?? 0, { nonNullable: true }),
    currency: new FormControl(initial?.currency ?? defaultCurrency, { nonNullable: true }),
  });
}

export function buildGoalOfferGroup(offer?: GoalOfferDefault): FormGroup<GoalOfferFormShape> {
  const product = offer?.product;
  return new FormGroup<GoalOfferFormShape>({
    type: new FormControl(offer?.type ?? 'freeShipping', { nonNullable: true }),
    orderDiscount: new FormGroup<GoalOrderDiscountFormShape>({
      discountType: new FormControl(offer?.orderDiscount?.discountType ?? 'fixedAmount', { nonNullable: true }),
      value: new FormControl(offer?.orderDiscount?.value ?? 10, { nonNullable: true }),
      labelText: new FormControl(offer?.orderDiscount?.labelText ?? '', { nonNullable: true }),
      labelColor: new FormControl(offer?.orderDiscount?.labelColor ?? DEFAULT_DISCOUNT_LABEL_COLOR, {
        nonNullable: true,
      }),
      autoApply: new FormControl(offer?.orderDiscount?.autoApply ?? false, { nonNullable: true }),
    }),
    product: new FormGroup<GoalProductOfferFormShape>({
      giftMode: new FormControl(product?.giftMode ?? 'single', { nonNullable: true }),
      productDisplayType: new FormControl(product?.productDisplayType ?? 'list', { nonNullable: true }),
      products: new FormControl(product?.products ?? [], { nonNullable: true }),
      hideIfProductIsInCart: new FormControl(product?.hideIfProductIsInCart ?? false, { nonNullable: true }),
      discountConfiguration: new FormGroup({
        enabled: new FormControl(product?.discountConfiguration?.enabled ?? false, { nonNullable: true }),
        discountType: new FormControl(product?.discountConfiguration?.discountType ?? 'fixedAmount', {
          nonNullable: true,
        }),
        value: new FormControl(product?.discountConfiguration?.value ?? 10, { nonNullable: true }),
        labelText: new FormControl(product?.discountConfiguration?.labelText ?? '', { nonNullable: true }),
        labelColor: new FormControl(product?.discountConfiguration?.labelColor ?? DEFAULT_DISCOUNT_LABEL_COLOR, {
          nonNullable: true,
        }),
      }),
    }),
  });
}

export function buildGoalGroup(
  goal?: GoalDefault,
  thresholdType: MinimumThresholdType = 'cart_value',
): FormGroup<GoalFormShape> {
  return new FormGroup<GoalFormShape>({
    id: new FormControl(goal?.id ?? `goal-${++goalIdCounter}`, { nonNullable: true }),
    enabled: new FormControl(goal?.enabled ?? true, { nonNullable: true }),
    thresholds: new FormArray<FormGroup<GoalThresholdFormShape>>(
      (goal?.thresholds ?? []).map((threshold) => buildGoalThresholdGroup('US', 'USD', threshold)),
    ),
    messageWhenNotMet: new FormControl(goal?.messageWhenNotMet ?? GOAL_NOT_MET_DEFAULTS[thresholdType], {
      nonNullable: true,
    }),
    messageWhenMet: new FormControl(goal?.messageWhenMet ?? '', { nonNullable: true }),
    offer: buildGoalOfferGroup(goal?.offer),
    appearance: buildGoalAppearanceGroup(goal?.appearance),
  });
}

function buildGoalAppearanceGroup(appearance?: GoalAppearanceDefault): FormGroup<GoalAppearanceFormShape> {
  return new FormGroup<GoalAppearanceFormShape>({
    iconUrl: new FormControl(appearance?.iconUrl ?? '', { nonNullable: true }),
    customIconUrl: new FormControl(appearance?.customIconUrl ?? '', { nonNullable: true }),
    // 'medium' (16px) is the same spacing the widget used to hard-code on its outer wrapper, so goals
    // saved before this control existed keep the look they had.
    messagePadding: new FormControl<GoalsPadding>(appearance?.messagePadding ?? 'medium', { nonNullable: true }),
    labelSize: new FormControl(appearance?.labelSize ?? 12, { nonNullable: true }),
    showLabelsInProgressBar: new FormControl(appearance?.showLabelsInProgressBar ?? true, { nonNullable: true }),
    firstLabel: new FormControl(appearance?.firstLabel ?? '{threshold}', { nonNullable: true }),
    firstLabelPosition: new FormControl(appearance?.firstLabelPosition ?? 'above_bar', { nonNullable: true }),
    secondLabel: new FormControl(appearance?.secondLabel ?? '', { nonNullable: true }),
    secondLabelPosition: new FormControl(appearance?.secondLabelPosition ?? 'above_bar', { nonNullable: true }),
    pillSource: new FormControl(appearance?.pillSource ?? 'first', { nonNullable: true }),
    pillText: new FormControl(appearance?.pillText ?? '', { nonNullable: true }),
    applySection: buildApplySectionGroup(appearance?.applySection),
  });
}

function buildApplySectionGroup(applySection?: GoalApplySectionDefault): FormGroup<GoalApplySectionFormShape> {
  const background = applySection?.background;
  const texts = applySection?.texts;
  const button = applySection?.button;
  return new FormGroup<GoalApplySectionFormShape>({
    background: new FormGroup({
      color: new FormControl(background?.color ?? '#FFFFFF', { nonNullable: true }),
      padding: new FormControl(background?.padding ?? 'none', { nonNullable: true }),
      cornerRadius: new FormControl(background?.cornerRadius ?? 0, { nonNullable: true }),
      border: new FormGroup({
        enabled: new FormControl(background?.border?.enabled ?? false, { nonNullable: true }),
        color: new FormControl(background?.border?.color ?? '#FFFFFF', { nonNullable: true }),
        width: new FormControl(background?.border?.width ?? 0, { nonNullable: true }),
      }),
    }),
    texts: new FormGroup({
      title: new FormControl(texts?.title ?? "Here's your gift", { nonNullable: true }),
      subtitle: new FormControl(texts?.subtitle ?? 'Click the button below to add it to your shopping cart', {
        nonNullable: true,
      }),
      orderDiscountMessage: new FormControl(texts?.orderDiscountMessage ?? '{discount} off your order!', {
        nonNullable: true,
      }),
      textColor: new FormControl(texts?.textColor ?? '#000000', { nonNullable: true }),
      fontSize: new FormControl(texts?.fontSize ?? 14, { nonNullable: true }),
    }),
    button: new FormGroup({
      label: new FormControl(button?.label ?? 'I want this!', { nonNullable: true }),
      backgroundColor: new FormControl<string | null>(button?.backgroundColor ?? null),
      textColor: new FormControl<string | null>(button?.textColor ?? null),
      hoverColor: new FormControl<string | null>(button?.hoverColor ?? null),
      cornerRadius: new FormControl<number | null>(button?.cornerRadius ?? null),
    }),
    image: new FormGroup({
      fit: new FormControl(applySection?.image?.fit ?? 'square', { nonNullable: true }),
      borderWidth: new FormControl(applySection?.image?.borderWidth ?? 0, { nonNullable: true }),
      cornerRadius: new FormControl(applySection?.image?.cornerRadius ?? 0, { nonNullable: true }),
      innerPadding: new FormControl(applySection?.image?.innerPadding ?? 0, { nonNullable: true }),
    }),
  });
}

export function createGoalsProgressBarFormGroup(
  initialSettings?: Record<string, unknown> | null,
): FormGroup<GoalsProgressBarFormShape> {
  const formGroup = new FormGroup<GoalsProgressBarFormShape>({
    minimumThresholdType: new FormControl(DEFAULTS.minimumThresholdType, { nonNullable: true }),
    appearance: new FormGroup<GoalsAppearanceFormShape>({
      barType: new FormControl(DEFAULTS.appearance.barType, { nonNullable: true }),
      bottomBorder: new FormGroup({
        enabled: new FormControl(DEFAULTS.appearance.bottomBorder.enabled, { nonNullable: true }),
        color: new FormControl(DEFAULTS.appearance.bottomBorder.color, { nonNullable: true }),
      }),
      pills: new FormGroup({
        enabled: new FormControl(DEFAULTS.appearance.pills.enabled, { nonNullable: true }),
        backgroundColor: new FormControl(DEFAULTS.appearance.pills.backgroundColor, { nonNullable: true }),
        textColor: new FormControl(DEFAULTS.appearance.pills.textColor, { nonNullable: true }),
        cornerRadius: new FormControl(DEFAULTS.appearance.pills.cornerRadius, { nonNullable: true }),
      }),
      background: new FormGroup({
        color: new FormControl(DEFAULTS.appearance.background.color, { nonNullable: true }),
        padding: new FormControl(DEFAULTS.appearance.background.padding, { nonNullable: true }),
        cornerRadius: new FormControl(DEFAULTS.appearance.background.cornerRadius, { nonNullable: true }),
        border: new FormGroup({
          enabled: new FormControl(DEFAULTS.appearance.background.border.enabled, { nonNullable: true }),
          color: new FormControl(DEFAULTS.appearance.background.border.color, { nonNullable: true }),
          width: new FormControl(DEFAULTS.appearance.background.border.width, { nonNullable: true }),
        }),
      }),
      progressBar: new FormGroup({
        color: new FormControl(DEFAULTS.appearance.progressBar.color, { nonNullable: true }),
        barWidth: new FormControl(DEFAULTS.appearance.progressBar.barWidth, { nonNullable: true }),
        cornerRadius: new FormControl(DEFAULTS.appearance.progressBar.cornerRadius, { nonNullable: true }),
      }),
    }),
    goals: new FormArray<FormGroup<GoalFormShape>>([], { validators: thresholdOrderValidator }),
    timer: createTimerFormGroup(),
    customCode: createCustomCodeFormGroup(undefined, GOALS_PROGRESS_BAR_STARTER_TEMPLATE),
  });

  const thresholdType =
    (initialSettings as { minimumThresholdType?: MinimumThresholdType })?.minimumThresholdType ??
    DEFAULTS.minimumThresholdType;

  const initialGoals = Array.isArray((initialSettings as { goals?: unknown })?.goals)
    ? ((initialSettings as { goals?: GoalDefault[] }).goals ?? [])
    : null;

  if (initialGoals && initialGoals.length) {
    for (const goal of initialGoals) {
      formGroup.controls.goals.push(buildGoalGroup(goal, thresholdType));
    }
  } else {
    formGroup.controls.goals.push(buildGoalGroup(undefined, thresholdType));
  }

  if (initialSettings) {
    const { goals: _goals, ...rest } = initialSettings as { goals?: unknown };
    formGroup.patchValue(rest, { emitEvent: false });
  }

  return formGroup;
}
